import React from 'react';
import { has, compose, map, cond, is, identity, join, isNil, always, T } from 'ramda';
import { Grid, Row, Col } from 'react-flexbox-grid';

import { formatDate, validate, formatHeaderDate } from 'lib/dateDisplay';
import { dateFromPartial, isPartialDate } from 'neuro-utils';

import { getLatestFinishedCommitData } from 'lib/documentHandling';

import HistoryEntryHeader from 'components/HistoryEntryHeader';
import { sectionTitle } from 'components/FormSection/FormSection.less';
import CommonHistoryEntryControls from 'routes/Documents/components/CommonHistoryEntryControls';

import { optTo, editLabel } from 'components/EditLog/EditLog.less';
import { labelAccessor, optionAccessor, sectionAccessor, valueExists } from './utils';

const handleOpt = (msg, opt) =>
  cond([
    [is(Number), identity],
    [isPartialDate, () => opt[0]],
    [is(Array), compose(join(', '), map(msg))],
    [isNil, always('')],
    [T, msg],
  ])(opt);

const makeOptMessageHandler = (accessor, formatMessage, messages) => msg =>
  cond([
    [has(accessor(msg)), x => formatMessage({ id: accessor(msg) })],
    [always(validate(msg)), always(formatDate(msg))],
    [has(`dictionary.${msg}`), x => formatMessage({ id: `dictionary.${msg}` })],
    [has(msg), x => formatMessage({ id: msg })],
    [T, always(msg)],
  ])(messages);

const renderEditsPerDay = (section, label, opt) => edits => {
  const titles = [...sections];

  const getTitle = field => {
    const matchingTitle = titles.find(t => fieldsBySection[t].includes(field));
    if (matchingTitle) {
      titles.splice(titles.indexOf(matchingTitle), 1);
      return matchingTitle;
    }
    return undefined;
  };

  return (
    <Grid fluid>
      {map(e => {
        const title = getTitle(e.field);
        return (
          <React.Fragment key={`${e.field}_${e.value}`}>
            {title && (
              <Row className={editLabel}>
                <Col xs={12} sm={4}>
                  <h6 className={sectionTitle} style={{ padding: 0 }}>
                    {section ? section(title) : title}
                  </h6>
                </Col>
              </Row>
            )}
            <Row className={editLabel}>
              <Col xs={12} sm={4}>
                <div>{label(e.field)}</div>
              </Col>
              <Col xs={4} sm={4}>
                <div className={optTo}>{handleOpt(opt, e.value)}</div>
              </Col>
            </Row>
          </React.Fragment>
        );
      })(edits)}
    </Grid>
  );
};

export const sorter = docs =>
  docs.sort(
    (d1, d2) =>
      (isPartialDate(d2?.content?.date) ? dateFromPartial(d2.content.date) : d2.createDate) -
      (isPartialDate(d1?.content?.date) ? dateFromPartial(d1.content.date) : d1.createDate),
  );

const fieldsBySection = {
  exercise: ['excerciseInterval'],
  nutrition: ['mealInterval', 'diet', 'specialDiet', 'vegetarianSince', 'eggsAndMilk', 'allergy'],
  smokingAndSnus: [
    'smoking',
    'smokingStartYear',
    'cigarettesADay',
    'smokingEndYear',
    'snus',
    'snusStartYear',
    'snusPortionsADay',
    'snusEndYear',
  ],
  alcohol: ['alcoholUse', 'alcoholMoreThan6Interval'],
  otherSubstances: [
    'usingOtherSubstances',
    'otherSubstanceMarijuanaUsageInterval',
    'otherSubstancesUsingOther',
    'otherSubstanceOtherName',
    'otherSubstanceOtherUsageInterval',
  ],
};

const sections = Object.keys(fieldsBySection);

const fields = [
  ...fieldsBySection.exercise,
  ...fieldsBySection.nutrition,
  ...fieldsBySection.smokingAndSnus,
  ...fieldsBySection.alcohol,
  ...fieldsBySection.otherSubstances,
];

const historyMapper =
  (name, edit) =>
  (d, { formatMessage, messages }) => {
    const { content } = d;
    const keys = fields.filter(key => valueExists(content[key]));

    const fieldValuePairs = keys.map(key => {
      return { field: key, value: content[key] };
    });

    const sectionMessage = x => formatMessage({ id: sectionAccessor(x || '') });
    const labelMessage = x => formatMessage({ id: labelAccessor(x || '') });
    const optionMessage = makeOptMessageHandler(optionAccessor, formatMessage, messages);

    return (
      <div key={d.documentId}>
        <HistoryEntryHeader title={formatHeaderDate(d.createDate)}>
          <CommonHistoryEntryControls action={() => edit(name, d.documentId, getLatestFinishedCommitData(d))} />
        </HistoryEntryHeader>
        {renderEditsPerDay(sectionMessage, labelMessage, optionMessage)(fieldValuePairs)}
      </div>
    );
  };

export default historyMapper;
